import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FolderIcon,
  XMarkIcon,
  BookmarkIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { useCollectionsStore } from '@/stores/collectionsStore';
import { useCollectionSharingStore } from '@/stores/collectionSharingStore';
import { CollectionTree } from '../common/CollectionTree';
import { CollectionManager } from '../common/CollectionManager';
import { CollectionInvitations } from '../collections/CollectionInvitations';
import type { CollectionQuery } from '@/services/api';
import { toast } from 'react-hot-toast';
import { COLLECTION_EVENTS, subscribeToCollectionEvent } from '@/utils/collectionEvents';

interface SavedQueriesWithCollectionsProps {
  onCollectionQuerySelect?: (query: CollectionQuery & { collectionId: string }) => void | Promise<void>;
  onCreateQuery?: () => void;
  selectedDatabase?: string;
  selectedSchema?: string;
  connectionType?: string;
  onClose?: () => void;
}


export const SavedQueriesWithCollections: React.FC<SavedQueriesWithCollectionsProps> = ({
  onCollectionQuerySelect,
  selectedDatabase,
  selectedSchema,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'my-collections' | 'invitations'>('my-collections');
  const [showCollectionManager, setShowCollectionManager] = useState(false);
  const [collectionManagerMode, setCollectionManagerMode] = useState<'create-collection' | 'create-query' | 'edit-collection' | 'create-folder'>('create-collection');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [selectedParentFolderId, setSelectedParentFolderId] = useState<string>('');
  
  // Stores and queries
  const { fetchCollections, refreshCollections } = useCollectionsStore();
  const { 
    invitations, 
    fetchInvitations 
  } = useCollectionSharingStore();
  
  // Debounce timeout reference
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Computed invitations count
  const invitationsCount = invitations.filter(inv => inv.status === 'pending').length;

  // Improved data fetching with better error handling
  const initializeData = useCallback(async () => {
    try {
      await Promise.all([
        fetchCollections(),
        fetchInvitations('pending')
      ]);
    } catch (error) {
      console.error('Failed to initialize data:', error);
      toast.error('Failed to load query library data');
    }
  }, [fetchCollections, fetchInvitations]);

  useEffect(() => {
    void initializeData();
  }, [initializeData]); // Store will handle deduplication

  // Force refresh collections when events are received
  const refreshData = useCallback(async () => {
    try {
      // Use refreshCollections to bypass deduplication and force refresh
      await Promise.all([
        refreshCollections(),
        fetchInvitations('pending')
      ]);
    } catch (error) {
      console.error('Failed to refresh data:', error);
      toast.error('Failed to refresh query library data');
    }
  }, [refreshCollections, fetchInvitations]);

  // Debounced refresh function to prevent multiple simultaneous requests
  const debouncedRefreshData = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Set new timeout
    refreshTimeoutRef.current = setTimeout(() => {
      void refreshData();
    }, 200); // 200ms debounce to ensure backend has updated
  }, [refreshData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Subscribe to collection events to refresh data when queries are updated
  useEffect(() => {
    const handleCollectionUpdate = () => {
      // Use debounced refresh to prevent multiple simultaneous requests
      debouncedRefreshData();
    };

    const unsubscribeUpdate = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_UPDATED, handleCollectionUpdate);
    const unsubscribeCreate = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_CREATED, handleCollectionUpdate);
    const unsubscribeDelete = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, handleCollectionUpdate);
    const unsubscribeMove = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_MOVED, handleCollectionUpdate);
    const unsubscribeFolderCreate = subscribeToCollectionEvent(COLLECTION_EVENTS.FOLDER_CREATED, handleCollectionUpdate);
    const unsubscribeFolderUpdate = subscribeToCollectionEvent(COLLECTION_EVENTS.FOLDER_UPDATED, handleCollectionUpdate);
    const unsubscribeFolderDelete = subscribeToCollectionEvent(COLLECTION_EVENTS.FOLDER_DELETED, handleCollectionUpdate);
    const unsubscribeCollectionCreate = subscribeToCollectionEvent(COLLECTION_EVENTS.COLLECTION_CREATED, handleCollectionUpdate);
    const unsubscribeCollectionUpdate = subscribeToCollectionEvent(COLLECTION_EVENTS.COLLECTION_UPDATED, handleCollectionUpdate);
    const unsubscribeCollectionDelete = subscribeToCollectionEvent(COLLECTION_EVENTS.COLLECTION_DELETED, handleCollectionUpdate);

    return () => {
      unsubscribeUpdate();
      unsubscribeCreate();
      unsubscribeDelete();
      unsubscribeMove();
      unsubscribeFolderCreate();
      unsubscribeFolderUpdate();
      unsubscribeFolderDelete();
      unsubscribeCollectionCreate();
      unsubscribeCollectionUpdate();
      unsubscribeCollectionDelete();
    };
  }, [debouncedRefreshData]);

  const handleCollectionQuerySelect = useCallback((query: CollectionQuery & { collectionId: string }) => {
    try {
      if (onCollectionQuerySelect) {
        void onCollectionQuerySelect(query);
      }
    } catch (error) {
      console.error('Failed to select collection query:', error);
      toast.error('Failed to load query');
    }
  }, [onCollectionQuerySelect]);



  const handleCreateCollection = useCallback(() => {
    setCollectionManagerMode('create-collection');
    setShowCollectionManager(true);
  }, []);

  const handleCreateQueryInCollection = useCallback((collectionId: string, _folderId?: string) => {
    setSelectedCollectionId(collectionId);
    setCollectionManagerMode('create-query');
    setShowCollectionManager(true);
  }, []);





  const renderCollectionsView = () => {
    switch (activeTab) {
      case 'invitations':
        return (
          <div className="p-4">
            <CollectionInvitations
              onInvitationAccepted={() => {
                void refreshData();
                setActiveTab('my-collections');
              }}
            />
          </div>
        );
      case 'my-collections':
        return (
          <CollectionTree
            onQuerySelect={handleCollectionQuerySelect}
            onCreateCollection={handleCreateCollection}
            onCreateQuery={handleCreateQueryInCollection}
            onCreateFolder={(collectionId, parentFolderId) => {
              setSelectedCollectionId(collectionId);
              setSelectedParentFolderId(parentFolderId || '');
              setCollectionManagerMode('create-folder');
              setShowCollectionManager(true);
            }}
            className="h-full"
          />
        );
      default:
        return null
    }
  };

  return (
    <>
      <div className="h-full bg-white dark:bg-gray-800 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <BookmarkIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Query Library
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            {activeTab === 'my-collections' && (
              <button type="button"
                onClick={handleCreateCollection}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                title="Create Collection"
              >
                <FolderIcon className="w-4 h-4" />
              </button>
            )}
            {onClose && (
              <button type="button"
                onClick={onClose}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                title="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => { setActiveTab('my-collections') }}
            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'my-collections'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Collections
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('invitations') }}
            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
              activeTab === 'invitations'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <BellIcon className="w-4 h-4" />
            Invitations
            {invitationsCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {invitationsCount}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {renderCollectionsView()}
        </div>
      </div>

      {/* Collection Manager Modal */}
      <CollectionManager
        isOpen={showCollectionManager}
        onClose={() => {
          setShowCollectionManager(false);
          setSelectedCollectionId('');
          setSelectedParentFolderId('');
          // Refresh data after modal closes to ensure consistency
          void initializeData();
        }}
        mode={collectionManagerMode}
        collectionId={selectedCollectionId}
        parentFolderId={selectedParentFolderId}
        initialQuery={
          collectionManagerMode === 'create-query' ? {
            name: '',
            query: '',
            databaseName: selectedDatabase,
            schemaName: selectedSchema
          } : undefined
        }
      />
    </>
  );
};